import { Col, notification, Pagination, Row, Space, Spin, Table } from 'antd';
import React, { useState } from 'react';
import { DownloadOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { FinancialOverviewFilter } from './FinancialOverviewFilter';
import { useSortedFinancialoverviewQuery } from '../hooks/useSortedFinancialoverviewQuery';
import PopConfirm from '../../../shared/Components/PopConfirm';
import { useOverviewLinkState } from '../linkstate/financialOverview.linkstate';
import { useGenerateFinancialOverviewMutation } from '../../../GraphQL/hooks/useGenerateFinancialOverviewMutation';
import { useMutation } from '@apollo/client';
import {
    CITRIN_DELETE_FINANCIAL_OVERVIEW,
    DeleteFinancialOverviewRequest,
    DeleteFinancialOverviewResponse,
} from '../../../GraphQL';
import { FinancialOverview } from '../../../shared/types';
import { convertDateToFormat } from '../../../shared/Utils';

const FinancialOverviewTable = () => {
    const {
        sortedFinancialOverview: financialOverview,
        refetch,
        loading,
        ...pagination
    } = useSortedFinancialoverviewQuery();
    const [{ selectedDateRange }] = useOverviewLinkState();
    const [IsLoading, setIsLoading] = useState(false);
    const { generateFinancialOverview, loading: loadingGenerate } = useGenerateFinancialOverviewMutation();
    const [deleteMutation] = useMutation<DeleteFinancialOverviewResponse, DeleteFinancialOverviewRequest>(
        CITRIN_DELETE_FINANCIAL_OVERVIEW
    );

    const handleGenerate = async () => {
        if (!selectedDateRange?.fromDate || !selectedDateRange?.toDate) {
            notification.warning({
                message: 'Please select a date range before generating.',
            });
            return;
        }
        try {
            await generateFinancialOverview({
                fromDate: selectedDateRange.fromDate,
                toDate: selectedDateRange.toDate,
            });
            // After generation, refresh table
            notification.success({
                message: 'Financial overview generated successfully.',
            });
            refetch();
        } catch (err) {
            console.error('Error generating financial overview', err);
            notification.error({
                message: 'Error generating financial overview.',
            });
        }
    };

    const handleRemoveFinancialOverview = (financialOverview: FinancialOverview) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: financialOverview._id } })
            .then(() => {
                refetch();
            })

            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const columns = [
        {
            title: 'Period',
            dataIndex: ['period', 'label'],
            key: 'period',
        },
        {
            title: 'Total Drivers',
            dataIndex: 'driversCount',
        },
        {
            title: 'Total Subscriptions',
            dataIndex: ['financialSummary', 'subscriptions'],
            key: 'financialSummary',
            render: (v) => `${Number(v ?? 0).toFixed(2)} TND`,
        },
        {
            title: 'Total Commissions',
            dataIndex: ['financialSummary', 'commissions'],
            key: 'commissions',
            render: (v) => `${Number(v ?? 0).toFixed(2)} TND`,
        },
        {
            title: 'TVA (19%)',
            dataIndex: ['financialSummary', 'taxOnRevenue'],
            render: (v) => `${Number(v ?? 0).toFixed(2)} TND`,
        },
        {
            title: 'Net Amount',
            dataIndex: ['financialSummary', 'netAmount'],
            render: (v) => `${Number(v ?? 0).toFixed(2)} TND`,
        },
        {
            title: 'Generated At',
            dataIndex: 'createdDate',
            key: 'createdDate',
            sorter: (a, b) => a.createdDate - b.createdDate,
            render: (text, record) => {
                return convertDateToFormat(record.createdDate, 'DD-MM-YYYY, HH:mm');
            },
        },
        {
            title: 'Actions',
            render: (_, row) => (
                <Space>
                    <PopConfirm
                        title={'Delete the financial overview'}
                        description={'Are you sure to delete this financial overview?'}
                        onConfirmTask={() => handleRemoveFinancialOverview(row)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={<DeleteOutlined style={{ fontSize: '16px', color: 'red' }} />}
                    />
                    <a
                        href={row.financialOverviewPdf?.url}
                        download={row.financialOverviewPdf?.name}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <DownloadOutlined style={{ fontSize: '16px', color: 'blue', cursor: 'pointer' }} />
                    </a>
                </Space>
            ),
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Financial Overviews ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            <div style={{ flex: '1 1 auto' }}>
                <Row gutter={[16, 6]} style={{ paddingBottom: 10, paddingTop: 20 }}>
                    <FinancialOverviewFilter onGenerate={handleGenerate} loading={loadingGenerate} />
                </Row>
                <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                    <Col xs={24}>
                        <Spin spinning={loading || loadingGenerate}>
                            <Table
                                dataSource={financialOverview}
                                columns={columns}
                                pagination={false}
                                rowKey={() => Math.random().toString()}
                                scroll={{ x: 800 }}
                            ></Table>
                        </Spin>
                    </Col>
                </Row>
                <Paginator />
            </div>
        </>
    );
};

export default FinancialOverviewTable;
